<template>
  <button class="favorite-btn" :class="{ 'in-wishlist': state.inWishlist }" @click="toggleFavorite">
    <svg class="icon-star">
      <use class="default" href="/sprite.svg#star"></use>
      <use class="hover" href="/sprite.svg#star-fill"></use>
    </svg>

    <span v-if="props.showCount" class="count-favorites">
      {{ state.subscribersCount }}
    </span>
  </button>
</template>

<script lang="ts" setup>
  import { defineProps, onMounted, onUnmounted, reactive, watch, withDefaults } from "vue";
  import Api from "@/services/api";
  import { useAuctionStore } from "@/stores/AuctionStore";
  import { useToast } from "primevue/usetoast";
  import { trans } from "@/utils/trans";

  const auctionStore = useAuctionStore();
  const toast = useToast();

  const props = withDefaults(
    defineProps<{
      lotIdProp: number;
      subscribersCountProp?: number;
      showCount?: boolean;
    }>(),
    {
      subscribersCountProp: 0,
      showCount: false
    }
  );

  const emit = defineEmits<{
    (e: "countChange", count: number): void;
  }>();

  const state = reactive({
    lotId: props.lotIdProp,
    inWishlist: auctionStore.user?.favorite_lots?.includes(props.lotIdProp) || false,
    subscribersCount: props.subscribersCountProp
  });

  watch(
    () => state.subscribersCount,
    (val) => {
      emit("countChange", val);
    },
    { immediate: true }
  );

  watch(
    () => auctionStore.user?.favorite_lots,
    (nv, ov) => {
      state.inWishlist = auctionStore.user?.favorite_lots?.includes(props.lotIdProp) || false;
    },
    { deep: true }
  );

  watch(
    () => props.subscribersCountProp,
    (val) => {
      state.subscribersCount = val || 0;
    }
  );

  watch(
    () => props.lotIdProp,
    (val, oldVal) => {
      state.lotId = val;
      state.inWishlist = auctionStore.user?.favorite_lots?.includes(val) || false;

      if (props.showCount) {
        if (oldVal) {
          unsubscribe(oldVal);
        }
        subscribe();
      }
    }
  );

  onMounted(() => {
    if (props.showCount) {
      subscribe();
    }
  });

  onUnmounted(() => {
    unsubscribe(state.lotId);
  });

  const subscribe = () => {
    window.Echo.channel(`lots.${props.lotIdProp}.favorite.count`).listen(
      ".changed",
      (e: { lotId: number; count: number }) => {
        state.subscribersCount = e.count || 0;
      }
    );
  };

  const unsubscribe = (lotId: number) => {
    if (props.showCount && lotId) {
      window.Echo.leave(`lots.${lotId}.favorite.count`);
    }
  };

  const toggleFavorite = (event: Event) => {
    event.stopPropagation();

    if (auctionStore.user?.id) {
      Api.post("/api/lots/" + state.lotId + "/toggle-favorite").catch((error) => {
        console.log(error);
      });
    } else {
      toast.add({
        severity: "warn",
        summary: trans("live.modals.auth_favorite.header"),
        detail: trans("live.modals.auth_favorite.message"),
        life: 6000
      });
    }
  };
</script>
